import React, { useState, useEffect, useCallback } from 'react';
import '../../shared/sheet-animations.css';
import { useScrollLock } from '../../../_utils/useScrollLock';
import { Card, CardHeader } from '../components/Card';
import { CloseIcon, CashIcon, TransferIcon } from '../icons/FinanceIcons';
import { financeService } from '../../../_services/finance.service';
import useWindowSize from '../../new-service/useWindowSize';
import { isUserSelfEmployed } from '../../cashup/CashupUtils';
import RefundBadge from '../../refunds/RefundBadge';
import { commissionRefund, signedMoney, money, METHOD_LABEL } from '../../refunds/refundLogic';

// A negative outstanding means the salon owes the SE (net of voucher-
// redemption credits) — render it as "Salon owes £X" rather than a raw
// minus, and tone it amber so it's clearly not a settled balance.
const fmtOutstanding = (v) => (Number(v) < 0
  ? `Salon owes £${Math.abs(Number(v)).toFixed(2)}`
  : `£${Number(v).toFixed(2)}`);
const outstandingColor = (v) => (Number(v) < 0 ? '#B45309' : (Number(v) > 0 ? '#1a1a1a' : '#059669'));

// Link icon for Quick Pay
const LinkIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

// Collection Modal Component
// With All locations on, a Commission Tracker row can belong to a sister site.
// Its site rides as `siteId` (the backend checks the caller's role there);
// `salonId` stays the device's active site.
const siteParamFor = (staff) => {
  const active = localStorage.getItem('salonId');
  return staff && staff.salonId && String(staff.salonId) !== String(active) ? { siteId: String(staff.salonId) } : {};
};

const CollectionModal = ({ staff, salonId, onClose, onSuccess, isSelfEmployed = false }) => {
  const { width: modalWidth } = useWindowSize();
  const isModalMobile = modalWidth <= 768;
  const [status, setStatus] = useState('selecting');
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [error, setError] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [commissionPaymentId, setCommissionPaymentId] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentFee, setPaymentFee] = useState(0);
  const [paymentNetAmount, setPaymentNetAmount] = useState(0);
  const [customAmount, setCustomAmount] = useState(staff ? staff.outstanding : 0);
  const [amountError, setAmountError] = useState(null);

  if (!staff) return null;

  const parsedAmount = parseFloat(customAmount);
  const amountValid = !isNaN(parsedAmount) && parsedAmount > 0;

  const handleQuickPay = async () => {
    if (!amountValid) { setAmountError('Please enter a valid amount'); return; }
    setPaymentMethod('quick_pay');
    setStatus('processing');
    setError(null);

    try {
      const response = await financeService.createCommissionPaymentLink({
        salonId,
        ...siteParamFor(staff),
        hairdresserId: staff.id,
        amount: parsedAmount
      });

      const data = response?.data?.data;
      if (data?.checkoutUrl) {
        setCheckoutUrl(data.checkoutUrl);
        setCommissionPaymentId(data.commissionPaymentId);
        setPaymentFee(data.fee || 0);
        setPaymentNetAmount(data.netAmount || 0);

        if (isSelfEmployed) {
          window.open(data.checkoutUrl, '_blank');
          setStatus('quick_pay');
        } else {
          setStatus('quick_pay');
        }
      } else {
        throw new Error('Failed to create payment link');
      }
    } catch (err) {
      console.error('Failed to create payment link:', err);
      setError(err?.response?.data?.responseData?.message || err.message || 'Failed to create payment link');
      setStatus('selecting');
    }
  };

  const handlePayment = async (method) => {
    if (!amountValid) { setAmountError('Please enter a valid amount'); return; }
    setPaymentMethod(method);
    setStatus('processing');
    setError(null);

    try {
      await financeService.collectCommission({
        salonId,
        ...siteParamFor(staff),
        hairdresserId: staff.id,
        amount: parsedAmount,
        paymentMethod: method,
        notes: `Commission collected via ${method}`
      });
      setStatus('success');
    } catch (err) {
      console.error('Failed to collect commission:', err);
      setError(err.message || 'Failed to process payment');
      setStatus('selecting');
    }
  };

  const handleCheckPayment = async () => {
    if (!commissionPaymentId) return;
    setCheckingStatus(true);
    try {
      const response = await financeService.checkCommissionPaymentStatus(commissionPaymentId);
      const data = response?.data?.data;
      if (data?.status === 'completed') {
        setStatus('success');
      } else {
        setError('Payment not yet received. Please wait for the staff member to complete payment.');
        setTimeout(() => setError(null), 4000);
      }
    } catch (err) {
      setError('Could not check payment status. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
    setCheckingStatus(false);
  };

  const handleCopyLink = () => {
    if (checkoutUrl) {
      navigator.clipboard.writeText(checkoutUrl).then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      });
    }
  };

  const handleDone = () => {
    onSuccess(staff.rowKey || staff.name, paymentMethod);
    onClose();
  };

  const methodLabels = {
    quick_pay: 'Payment link sent',
    cash: 'Payment marked as cash',
    other: 'Payment marked as other'
  };

  const qrCodeUrl = checkoutUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(checkoutUrl)}` : null;

  return (
    <>
      <div className="sheet-anim-backdrop" style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 100 }} onClick={status === 'selecting' ? onClose : undefined} />
      <div className={isModalMobile ? 'sheet-anim-up' : 'sheet-anim-center'} style={isModalMobile ? {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        borderRadius: '16px 16px 0 0',
        zIndex: 101,
        overflow: 'hidden',
        boxShadow: '0 -4px 40px rgba(0, 0, 0, 0.15)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        maxHeight: '90vh',
        overflowY: 'auto'
      } : {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '420px',
        zIndex: 101,
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        {status === 'success' ? (
          <div style={{ padding: '3rem 2rem', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <svg style={{ width: '40px', height: '40px', color: '#059669' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 8px 0' }}>Payment {isSelfEmployed ? 'Complete' : 'Collected'}!</h2>
            <p style={{ fontSize: '15px', color: '#737373', margin: '0 0 8px 0' }}>{methodLabels[paymentMethod]}</p>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#059669', margin: '0 0 24px 0' }}>£{parsedAmount.toFixed(2)}</p>
            {!isSelfEmployed && <p style={{ fontSize: '14px', color: '#737373', margin: '0 0 24px 0' }}>from <strong>{staff.name}</strong></p>}
            <button onClick={handleDone} style={{ width: '100%', padding: '14px 24px', backgroundColor: '#1a1a1a', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>Done</button>
          </div>
        ) : status === 'quick_pay' ? (
          <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>{isSelfEmployed ? 'Pay Commission' : 'Quick Pay Link'}</h2>
              <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                <CloseIcon size={20} />
              </button>
            </div>

            {isSelfEmployed ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <p style={{ fontSize: '15px', color: '#737373', margin: '0 0 16px 0' }}>
                  A payment page has been opened in a new tab. Complete the payment there.
                </p>
                <p style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 24px 0' }}>£{parsedAmount.toFixed(2)}</p>
                <button onClick={() => window.open(checkoutUrl, '_blank')} style={{ width: '100%', padding: '14px 24px', backgroundColor: 'white', color: '#29282D', border: '1px solid #e5e5e5', borderRadius: '12px', fontSize: '15px', fontWeight: '500', cursor: 'pointer', marginBottom: '10px' }}>
                  Open Payment Page Again
                </button>
                <button onClick={handleCheckPayment} disabled={checkingStatus} style={{ width: '100%', padding: '14px 24px', backgroundColor: '#1a1a1a', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', opacity: checkingStatus ? 0.7 : 1 }}>
                  {checkingStatus ? 'Checking...' : 'I\'ve Paid - Confirm'}
                </button>
              </div>
            ) : (
              <>
                {/* QR Code */}
                <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                  <p style={{ fontSize: '14px', color: '#737373', margin: '0 0 16px 0' }}>
                    Show this QR code to <strong>{staff.name}</strong> to scan and pay
                  </p>
                  {qrCodeUrl && (
                    <div style={{ display: 'inline-block', padding: '16px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e5e5', marginBottom: '16px' }}>
                      <img src={qrCodeUrl} alt="Payment QR Code" style={{ width: '200px', height: '200px', display: 'block' }} />
                    </div>
                  )}
                  <p style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 4px 0' }}>£{parsedAmount.toFixed(2)}</p>
                  <p style={{ fontSize: '13px', color: '#737373', margin: '0 0 16px 0' }}>Fee: £{paymentFee.toFixed(2)} | You receive: £{paymentNetAmount.toFixed(2)}</p>
                </div>

                {/* Copy Link */}
                <button onClick={handleCopyLink} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '12px 24px', backgroundColor: linkCopied ? '#d1fae5' : '#f5f5f5', color: linkCopied ? '#059669' : '#1a1a1a', border: '1px solid #e5e5e5', borderRadius: '12px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', marginBottom: '10px', transition: 'all 0.2s' }}>
                  {linkCopied ? (
                    <>
                      <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Link Copied!
                    </>
                  ) : (
                    <>
                      <LinkIcon size={16} />
                      Copy Payment Link
                    </>
                  )}
                </button>

                {/* Check Payment */}
                <button onClick={handleCheckPayment} disabled={checkingStatus} style={{ width: '100%', padding: '14px 24px', backgroundColor: '#1a1a1a', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', opacity: checkingStatus ? 0.7 : 1 }}>
                  {checkingStatus ? 'Checking...' : 'Confirm Payment Received'}
                </button>
              </>
            )}

            {error && (
              <p style={{ fontSize: '13px', color: '#ef4444', margin: '12px 0 0 0', textAlign: 'center' }}>{error}</p>
            )}
          </div>
        ) : status === 'processing' ? (
          <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
            <div style={{ width: '60px', height: '60px', border: '3px solid #e5e5e5', borderTopColor: '#007EA0', borderRadius: '50%', margin: '0 auto 1.5rem', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ fontSize: '16px', color: '#737373', margin: 0 }}>Processing...</p>
          </div>
        ) : (
          <>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e5e5' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>{isSelfEmployed ? 'Pay Commission' : 'Collect Commission'}</h2>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                  <CloseIcon size={20} />
                </button>
              </div>
            </div>
            <div style={{ padding: '1.5rem', backgroundColor: '#fafafa', borderBottom: '1px solid #e5e5e5' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: '600' }}>
                  {staff.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>{staff.name}</p>
                  <p style={{ fontSize: '13px', color: '#737373', margin: 0 }}>Commission outstanding</p>
                </div>
              </div>
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1rem', border: amountError ? '1px solid #ef4444' : '1px solid #e5e5e5' }}>
                <p style={{ fontSize: '13px', color: '#737373', margin: '0 0 8px 0' }}>{isSelfEmployed ? 'Amount to pay' : 'Amount to collect'}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a' }}>£</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={customAmount}
                    onChange={e => { setCustomAmount(e.target.value); setAmountError(null); }}
                    style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', border: 'none', outline: 'none', width: '100%', background: 'transparent', padding: 0 }}
                  />
                </div>
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: '6px 0 0 0' }}>
                  Outstanding: £{staff.outstanding.toFixed(2)}
                  {parsedAmount < staff.outstanding && parsedAmount > 0 && (
                    <span style={{ marginLeft: '8px', color: '#f59e0b' }}>Partial payment</span>
                  )}
                </p>
                {amountError && <p style={{ fontSize: '12px', color: '#ef4444', margin: '4px 0 0 0' }}>{amountError}</p>}
              </div>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {error && (
                <p style={{ fontSize: '13px', color: '#ef4444', margin: '0 0 12px 0' }}>{error}</p>
              )}
              <p style={{ fontSize: '13px', fontWeight: '500', color: '#737373', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment Method</p>
              {isSelfEmployed ? (
                <button onClick={handleQuickPay} style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '14px 16px', border: '1px solid #e5e5e5', borderRadius: '12px', background: 'white', cursor: 'pointer', marginBottom: '10px', transition: 'all 0.15s' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: '#007EA0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                    <LinkIcon size={20} />
                  </div>
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <p style={{ fontSize: '15px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>Pay Now</p>
                    <p style={{ fontSize: '13px', color: '#737373', margin: '2px 0 0 0' }}>Pay via secure payment link</p>
                  </div>
                </button>
              ) : (
                <>
                  {[
                    { method: 'quick_pay', icon: LinkIcon, color: '#007EA0', title: 'Quick Pay (Payment Link)', subtitle: 'Generate QR code for staff to scan & pay', handler: handleQuickPay },
                    { method: 'cash', icon: CashIcon, color: '#f59e0b', title: 'Mark received in cash', subtitle: 'Record cash payment', handler: () => handlePayment('cash') },
                    { method: 'other', icon: TransferIcon, color: '#737373', title: 'Mark received in other', subtitle: 'Bank transfer, cheque, etc.', handler: () => handlePayment('other') }
                  ].map(({ method, icon: Icon, color, title, subtitle, handler }) => (
                    <button key={method} onClick={handler} style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '14px 16px', border: '1px solid #e5e5e5', borderRadius: '12px', background: 'white', cursor: 'pointer', marginBottom: '10px', transition: 'all 0.15s' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                        <Icon size={20} />
                      </div>
                      <div style={{ textAlign: 'left', flex: 1 }}>
                        <p style={{ fontSize: '15px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{title}</p>
                        <p style={{ fontSize: '13px', color: '#737373', margin: '2px 0 0 0' }}>{subtitle}</p>
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

// Commission Detail Sidebar Component
const CommissionDetailSidebar = ({ staff, onClose }) => {
  const { width: sidebarWidth_ } = useWindowSize();
  const isSidebarMobile = sidebarWidth_ <= 768;
  const [activeTab, setActiveTab] = useState('bookings');
  const [summary, setSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [refundItems, setRefundItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsHasMore, setPaymentsHasMore] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsLoadingMore, setPaymentsLoadingMore] = useState(false);

  // Shared ref-counted scroll lock while the detail panel is open. The old
  // inline version also cleared overflow unconditionally on every close,
  // which could unlock a different overlay's lock.
  useScrollLock(!!staff);

  const fetchBookings = useCallback(async (skip = 0, append = false) => {
    if (!staff) return;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const salonId = localStorage.getItem('salonId');
      const response = await financeService.getCommissionDetail({
        salonId,
        ...siteParamFor(staff),
        hairdresserId: staff.id,
        skip,
        limit: 10
      });
      const d = response?.data?.data;
      if (d) {
        setSummary(d.summary);
        // Refunds made in the period on cash / gift-card money (docs/refunds.md):
        // what moved the balance, itemised.
        if (!append) setRefundItems(Array.isArray(d.refunds) ? d.refunds : []);
        setBookings(prev => append ? [...prev, ...d.bookings] : d.bookings);
        setHasMore(d.hasMore);
      }
    } catch (err) {
      console.error('Failed to fetch commission detail:', err);
      setError('Failed to load commission details');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [staff]);

  const fetchPayments = useCallback(async (skip = 0, append = false) => {
    if (!staff) return;
    if (append) setPaymentsLoadingMore(true); else setPaymentsLoading(true);
    try {
      const salonId = localStorage.getItem('salonId');
      const response = await financeService.getCommissionPaymentsList({
        salonId,
        ...siteParamFor(staff),
        hairdresserId: staff.id,
        skip,
        limit: 20
      });
      const d = response?.data?.data;
      if (d) {
        setPayments(prev => append ? [...prev, ...d.payments] : d.payments);
        setPaymentsTotal(d.total);
        setPaymentsHasMore(d.hasMore);
      }
    } catch (err) {
      console.error('Failed to fetch commission payments:', err);
    } finally {
      setPaymentsLoading(false);
      setPaymentsLoadingMore(false);
    }
  }, [staff]);

  useEffect(() => {
    setSummary(null);
    setBookings([]);
    setRefundItems([]);
    setHasMore(false);
    fetchBookings(0, false);
  }, [fetchBookings]);

  useEffect(() => {
    setPayments([]);
    setPaymentsTotal(0);
    setPaymentsHasMore(false);
    fetchPayments(0, false);
  }, [fetchPayments]);

  if (!staff) return null;

  const formatCurrency = (val) => val != null ? `\u00A3${Number(val).toFixed(2)}` : '\u00A30.00';

  return (
    <div className="sheet-anim-side" style={{
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: isSidebarMobile ? '100%' : '480px',
      backgroundColor: 'white',
      boxShadow: '-8px 0 30px rgba(0, 0, 0, 0.12)',
      zIndex: 1001,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>Commission Details</h2>
          <p style={{ fontSize: '13px', color: '#9ca3af', margin: '4px 0 0 0' }}>{staff.name}</p>
        </div>
        <button
          onClick={onClose}
          style={{ background: '#f3f4f6', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '8px' }}
        >
          <CloseIcon size={20} />
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e5e5', padding: '0 24px' }}>
        {[{ id: 'bookings', label: 'Bookings' }, { id: 'payments', label: `Payments${paymentsTotal > 0 ? ` (${paymentsTotal})` : ''}` }].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 0',
              marginRight: '24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #007EA0' : '2px solid transparent',
              color: activeTab === tab.id ? '#007EA0' : '#737373',
              fontSize: '14px',
              fontWeight: activeTab === tab.id ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.15s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content - Scrollable */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
        <>
        {loading && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ width: '28px', height: '28px', border: '3px solid #e5e5e5', borderTopColor: '#007EA0', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ fontSize: '13px', color: '#737373' }}>Loading details...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {error && (
          <div style={{ padding: '16px', backgroundColor: '#fef2f2', borderRadius: '10px', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', color: '#dc2626', margin: 0 }}>{error}</p>
          </div>
        )}

        {!loading && !error && summary && (
          <>
            {/* Commission Summary Card */}
            <div style={{ backgroundColor: '#1a1a1a', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
              <p style={{ fontSize: '28px', fontWeight: '700', color: '#ffffff', margin: '0 0 12px 0' }}>
                {formatCurrency(summary.totalCommission)}
              </p>
              <p style={{ fontSize: '13px', color: '#9ca3af', margin: '0 0 16px 0' }}>
                Total commission from {summary.bookingCount} booking{summary.bookingCount !== 1 ? 's' : ''}
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '12px' }}>
                  <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 4px 0' }}>Services</p>
                  <p style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: 0 }}>{formatCurrency(summary.serviceCommission)}</p>
                </div>
                <div style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '12px' }}>
                  <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 4px 0' }}>Products</p>
                  <p style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: 0 }}>{formatCurrency(summary.productCommission)}</p>
                </div>
                <div style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '12px' }}>
                  <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 4px 0' }}>Vouchers</p>
                  <p style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: 0 }}>{formatCurrency(summary.voucherCommission)}</p>
                </div>
                {commissionRefund(summary) !== 0 && (
                  <div style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '12px' }}>
                    <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 4px 0' }}>Refunds</p>
                    <p style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: 0 }}>{signedMoney(commissionRefund(summary))}</p>
                  </div>
                )}
                {Number(summary.discountCodeCredit) !== 0 && !isNaN(Number(summary.discountCodeCredit)) && summary.discountCodeCredit != null && (
                  <div style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '12px' }}>
                    <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 4px 0' }}>Discount codes</p>
                    <p style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: 0 }}>{signedMoney(summary.discountCodeCredit)}</p>
                  </div>
                )}
                {summary.voucherRedemptionCredit < 0 && (
                  <div style={{ flex: 1, backgroundColor: 'rgba(180,83,9,0.25)', borderRadius: '8px', padding: '12px' }}>
                    <p style={{ fontSize: '11px', color: '#fcd9b6', margin: '0 0 4px 0' }}>Redemption</p>
                    <p style={{ fontSize: '16px', fontWeight: '600', color: '#fcd9b6', margin: 0 }}>{formatCurrency(summary.voucherRedemptionCredit)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Refunds that moved this balance (cash, other, gift card) */}
            {refundItems.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Refunds</h3>
                <div style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '4px 16px' }}>
                  {refundItems.map((r, idx) => (
                    <div key={r.refundId || idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', padding: '10px 0', borderTop: idx ? '1px solid #eee' : 'none' }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontSize: '13px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>
                          {money(r.amount)} {r.method === 'voucher' ? 'back to a gift card' : `refunded in ${(METHOD_LABEL[r.method] || 'other').toLowerCase()}`}
                        </p>
                        <p style={{ fontSize: '11px', color: '#9ca3af', margin: '2px 0 0 0' }}>
                          {r.at ? new Date(r.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                          {r.reason ? ` · ${r.reason}` : ''}
                        </p>
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', whiteSpace: 'nowrap' }}>{signedMoney(r.adjustment || 0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Individual Bookings */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Bookings</h3>
              {bookings.map((booking, idx) => (
                <div key={booking.bookingId || idx} style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '16px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>{booking.clientName}<RefundBadge sale={booking} small /></p>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>
                        {new Date(booking.bookingDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>{formatCurrency(booking.totalCommission)}</p>
                  </div>

                  {/* Commission line items */}
                  <div style={{ borderTop: '1px solid #e5e5e5', paddingTop: '8px' }}>
                    {booking.serviceCommission > 0 && (
                      <div style={{ padding: '6px 0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#1a1a1a' }}>Services</span>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(booking.serviceCommission)}</span>
                        </div>
                        {booking.serviceCommissionMixed ? (
                          // Services carry different per-service rates — show each.
                          booking.services?.map((svc, si) => (
                            <div key={si} style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '11px', color: '#9ca3af' }}>{svc.name || 'Service'}</span>
                              <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                {formatCurrency(svc.price)} @ {svc.commissionRate ?? booking.serviceCommissionRate ?? 0}%
                              </span>
                            </div>
                          ))
                        ) : (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                              {booking.services?.map(s => s.name).filter(Boolean).join(', ') || 'Service'}
                            </span>
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                              {formatCurrency(booking.serviceTotal)} @ {booking.serviceCommissionRate || 0}%
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {booking.productCommission > 0 && (
                      <div style={{ padding: '6px 0', borderTop: booking.serviceCommission > 0 ? '1px solid #f0f0f0' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#1a1a1a' }}>Products</span>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(booking.productCommission)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                            {booking.products?.map(p => p.name).filter(Boolean).join(', ') || 'Product'}
                          </span>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                            {formatCurrency(booking.productTotal)} @ {booking.products?.[0]?.commissionRate || 0}%
                          </span>
                        </div>
                      </div>
                    )}
                    {booking.voucherCommission > 0 && (
                      <div style={{ padding: '6px 0', borderTop: (booking.serviceCommission > 0 || booking.productCommission > 0) ? '1px solid #f0f0f0' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#1a1a1a' }}>Vouchers</span>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(booking.voucherCommission)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                            {booking.vouchers?.map(v => v.code).filter(Boolean).join(', ') || 'Voucher'}
                          </span>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                            {formatCurrency(booking.voucherTotal)} @ {booking.vouchers?.[0]?.commissionRate || 0}%
                          </span>
                        </div>
                      </div>
                    )}
                    {commissionRefund(booking) !== 0 && (
                      <div style={{ padding: '6px 0', borderTop: (booking.serviceCommission > 0 || booking.productCommission > 0 || booking.voucherCommission > 0) ? '1px solid #f0f0f0' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#b42318' }}>Refunds</span>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#b42318' }}>{signedMoney(commissionRefund(booking))}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>Their share of money refunded to the client</span>
                        </div>
                      </div>
                    )}
                    {Number(booking.discountCodeCredit) !== 0 && booking.discountCodeCredit != null && !isNaN(Number(booking.discountCodeCredit)) && (
                      <div style={{ padding: '6px 0', borderTop: (booking.serviceCommission > 0 || booking.productCommission > 0 || booking.voucherCommission > 0) ? '1px solid #f0f0f0' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#1a1a1a' }}>Discount code{booking.promoCode ? ` ${booking.promoCode}` : ''}</span>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#1a1a1a' }}>{signedMoney(booking.discountCodeCredit)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>{Number(booking.discountCodeCredit) < 0 ? 'The salon’s share of the code, taken off what’s owed to the salon' : 'The stylist’s share of the code, added to what’s owed to the salon'}</span>
                        </div>
                      </div>
                    )}
                    {booking.voucherRedemptionCredit < 0 && (
                      <div style={{ padding: '6px 0', borderTop: (booking.serviceCommission > 0 || booking.productCommission > 0 || booking.voucherCommission > 0) ? '1px solid #f0f0f0' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#B45309' }}>Voucher redemption</span>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#B45309' }}>{formatCurrency(booking.voucherRedemptionCredit)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>Salon-held voucher used on this booking — owed to staff</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {bookings.length === 0 && (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <p style={{ fontSize: '13px', color: '#9ca3af' }}>No commission bookings found</p>
                </div>
              )}

              {hasMore && (
                <button
                  onClick={() => fetchBookings(bookings.length, true)}
                  disabled={loadingMore}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e5e5e5',
                    borderRadius: '9999px',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1a1a1a',
                    backgroundColor: 'white',
                    cursor: loadingMore ? 'default' : 'pointer',
                    marginTop: '8px'
                  }}
                >
                  {loadingMore ? 'Loading...' : 'Load more'}
                </button>
              )}
            </div>
          </>
        )}
        </>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <>
            {paymentsLoading && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ width: '28px', height: '28px', border: '3px solid #e5e5e5', borderTopColor: '#007EA0', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 0.8s linear infinite' }} />
                <p style={{ fontSize: '13px', color: '#737373' }}>Loading payments...</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            {!paymentsLoading && payments.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <p style={{ fontSize: '14px', color: '#9ca3af', margin: 0 }}>No payments recorded yet</p>
              </div>
            )}
            {payments.map((payment, idx) => {
              const methodLabel = { cash: 'Cash', other: 'Bank / Other', quick_pay: 'Payment Link' }[payment.paymentMethod] || payment.paymentMethod;
              return (
                <div key={payment.id || idx} style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '14px 16px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0 }}>{methodLabel}</p>
                    <p style={{ fontSize: '12px', color: '#9ca3af', margin: '3px 0 0 0' }}>
                      {new Date(payment.paidAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {payment.notes && <span style={{ marginLeft: '8px' }}>· {payment.notes}</span>}
                    </p>
                  </div>
                  <p style={{ fontSize: '15px', fontWeight: '600', color: '#059669', margin: 0 }}>{formatCurrency(payment.amount)}</p>
                </div>
              );
            })}
            {paymentsHasMore && (
              <button
                onClick={() => fetchPayments(payments.length, true)}
                disabled={paymentsLoadingMore}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '12px', border: '1px solid #e5e5e5', borderRadius: '9999px', fontSize: '13px', fontWeight: '500', color: '#1a1a1a', backgroundColor: 'white', cursor: paymentsLoadingMore ? 'default' : 'pointer', marginTop: '8px' }}
              >
                {paymentsLoadingMore ? 'Loading...' : 'Load more'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const CommissionsView = ({ staffFilter, staffList, salonIds = null, selectedSites = [] }) => {
  const { width } = useWindowSize();
  const isMobile = width <= 768;
  const salonId = localStorage.getItem('salonId');
  const isSelfEmp = isUserSelfEmployed();

  const [commissionData, setCommissionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commissionExpanded, setCommissionExpanded] = useState(false);
  const [collectionModal, setCollectionModal] = useState({ open: false, staff: null });
  const [selectedCommissionStaff, setSelectedCommissionStaff] = useState(null);
  const [recentlyCollected, setRecentlyCollected] = useState(null);

  const sortedCommissionData = [...commissionData]
    .filter(staff => staffFilter === 'all' || staff.id === staffFilter)
    .sort((a, b) => b.outstanding - a.outstanding);

  // One row per person per site (All locations): a stylist renting at two
  // sites owes each site separately, and is collected at each site.
  const mapCommissionData = (data) => {
    return Array.isArray(data) ? data.map(c => ({
      id: c.hairdresserId,
      rowKey: `${c.hairdresserId}:${c.salonId || ''}`,
      salonId: c.salonId || null,
      salonName: c.salonName || '',
      name: c.name,
      owed: c.owed || 0,
      paid: c.paid || 0,
      outstanding: c.outstanding || 0,
      serviceCommission: c.serviceCommission || 0,
      productCommission: c.productCommission || 0,
      voucherCommission: c.voucherCommission || 0,
      bookingCount: c.bookingCount || 0,
      commissionRate: c.commissionRate || 0,
      // Taken off by refunds (docs/refunds.md); absent = none.
      refunds: commissionRefund(c)
    })) : [];
  };

  const fetchCommissionData = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    try {
      // No date filters — always show all-time outstanding
      const res = await financeService.getCommissionOutstanding({
        salonId,
        ...(salonIds ? { salonIds } : {}),
        hairdresserId: staffFilter !== 'all' ? staffFilter : undefined
      });
      if (res?.data?.data) {
        setCommissionData(mapCommissionData(res.data.data));
      }
    } catch (err) {
      console.error('Failed to fetch commission data:', err);
    } finally {
      setLoading(false);
    }
  }, [salonId, salonIds, staffFilter]);

  useEffect(() => {
    fetchCommissionData();
  }, [fetchCommissionData]);

  const handleCollectionSuccess = (rowKey, method) => {
    setCommissionData(prev => prev.map(s =>
      s.rowKey === rowKey ? { ...s, paid: s.owed, outstanding: 0 } : s
    ));
    setRecentlyCollected(rowKey);
    setTimeout(() => setRecentlyCollected(null), 2000);
    // Refresh commission data
    financeService.getCommissionOutstanding({
      salonId,
      ...(salonIds ? { salonIds } : {}),
      hairdresserId: staffFilter !== 'all' ? staffFilter : undefined
    }).then(res => {
      if (res?.data?.data) {
        setCommissionData(mapCommissionData(res.data.data));
      }
    }).catch(console.error);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
        <div style={{ width: '32px', height: '32px', border: '3px solid #e5e5e5', borderTopColor: '#007EA0', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      {/* Commission Tracker */}
      <Card noPadding style={{ overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 16px 24px' }}>
          <CardHeader title="Commission Tracker" tooltip="Track and manage staff commission payments, including earned amounts and outstanding balances" />
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '-12px 0 0 0' }}>
            All-time outstanding balances
          </p>
        </div>

        {isMobile ? (
          /* Mobile: Card-based list */
          <div className="forms-mobile-list">
            {(commissionExpanded ? sortedCommissionData : sortedCommissionData.slice(0, 4)).map((staff) => {
              const isRecentlyCollected = recentlyCollected === staff.rowKey;
              return (
                <div
                  key={staff.rowKey}
                  className="forms-mobile-card"
                  onClick={() => setSelectedCommissionStaff(staff)}
                  style={isRecentlyCollected ? { backgroundColor: '#d1fae5', transition: 'background-color 0.5s ease' } : {}}
                >
                  <div className="forms-mobile-card-left">
                    <div className="forms-mobile-card-info">
                      <span className="forms-mobile-card-name">{staff.name}{staff.salonName ? <span style={{ fontWeight: 400, color: '#8E8E93' }}> · {staff.salonName}</span> : null}</span>
                      <span className="forms-mobile-card-meta">
                        Owed £{staff.owed.toFixed(2)}
                        <span className="forms-mobile-card-dot"> · </span>
                        Paid £{staff.paid.toFixed(2)}
                        {staff.refunds !== 0 && <><span className="forms-mobile-card-dot"> · </span>Refunds {signedMoney(staff.refunds)}</>}
                      </span>
                    </div>
                  </div>
                  <div className="forms-mobile-card-right" style={{ alignItems: 'flex-end', gap: '6px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: outstandingColor(staff.outstanding) }}>
                      {fmtOutstanding(staff.outstanding)}
                    </span>
                    {isRecentlyCollected ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        backgroundColor: '#059669', color: '#FFFFFF',
                        padding: '4px 12px', fontSize: '12px', fontWeight: '500', borderRadius: '9999px'
                      }}>
                        <svg style={{ width: '12px', height: '12px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Collected
                      </span>
                    ) : staff.outstanding > 0 ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); setCollectionModal({ open: true, staff }); }}
                        style={{ backgroundColor: '#29282D', color: '#FFFFFF', padding: '4px 12px', fontSize: '12px', fontWeight: '500', borderRadius: '9999px', border: 'none', cursor: 'pointer' }}
                      >
                        {isSelfEmp ? 'Pay' : 'Collect'}
                      </button>
                    ) : staff.outstanding < 0 ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        backgroundColor: '#FEF3C7', color: '#B45309',
                        padding: '4px 12px', fontSize: '12px', fontWeight: '500', borderRadius: '9999px'
                      }}>
                        Owed to staff
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        backgroundColor: '#d1fae5', color: '#059669',
                        padding: '4px 12px', fontSize: '12px', fontWeight: '500', borderRadius: '9999px'
                      }}>
                        <svg style={{ width: '12px', height: '12px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Paid
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Desktop: Table */
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '500px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
                  <th style={{ textAlign: 'left', paddingBottom: '12px', fontWeight: '500', color: '#1a1a1a', paddingLeft: '24px', fontSize: '14px' }}></th>
                  <th style={{ textAlign: 'center', paddingBottom: '12px', fontWeight: '500', color: '#1a1a1a', fontSize: '14px' }}>Owed</th>
                  <th style={{ textAlign: 'center', paddingBottom: '12px', fontWeight: '500', color: '#1a1a1a', fontSize: '14px' }}>Paid</th>
                  <th style={{ textAlign: 'center', paddingBottom: '12px', fontWeight: '500', color: '#1a1a1a', fontSize: '14px' }}>Outstanding</th>
                  <th style={{ paddingBottom: '12px', paddingRight: '24px' }}></th>
                </tr>
              </thead>
              <tbody>
                {(commissionExpanded ? sortedCommissionData : sortedCommissionData.slice(0, 4)).map((staff, idx) => {
                  const isRecentlyCollected = recentlyCollected === staff.rowKey;
                  return (
                    <tr
                      key={staff.rowKey}
                      onClick={() => setSelectedCommissionStaff(staff)}
                      style={{
                        backgroundColor: isRecentlyCollected ? '#d1fae5' : (idx % 2 === 0 ? '#F7F7F7' : '#FFFFFF'),
                        transition: 'background-color 0.5s ease',
                        cursor: 'pointer'
                      }}
                    >
                      <td style={{ padding: '16px 24px', fontWeight: '500', color: '#1a1a1a', whiteSpace: 'nowrap', fontSize: '14px' }}>
                        {staff.name}
                        {staff.salonName ? <div style={{ fontSize: '12px', fontWeight: 400, color: '#8E8E93' }}>{staff.salonName}</div> : null}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center', color: '#1a1a1a', fontSize: '14px' }}>
                        £{staff.owed.toFixed(2)}
                        {staff.refunds !== 0 && <div style={{ fontSize: '12px', color: '#9ca3af' }}>includes {signedMoney(staff.refunds)} from refunds</div>}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center', color: '#1a1a1a', fontSize: '14px' }}>£{staff.paid.toFixed(2)}</td>
                      <td style={{ padding: '16px', textAlign: 'center', fontWeight: '500', color: outstandingColor(staff.outstanding), fontSize: '14px' }}>
                        {fmtOutstanding(staff.outstanding)}
                      </td>
                      <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                        {isRecentlyCollected ? (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#059669',
                            color: '#FFFFFF',
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderRadius: '9999px'
                          }}>
                            <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Collected
                          </span>
                        ) : staff.outstanding > 0 ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); setCollectionModal({ open: true, staff }); }}
                            style={{ backgroundColor: '#29282D', color: '#FFFFFF', padding: '8px 20px', fontSize: '14px', fontWeight: '500', borderRadius: '9999px', border: 'none', cursor: 'pointer' }}
                          >
                            {isSelfEmp ? 'Pay' : 'Collect'}
                          </button>
                        ) : staff.outstanding < 0 ? (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#FEF3C7',
                            color: '#B45309',
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderRadius: '9999px'
                          }}>
                            Owed to staff
                          </span>
                        ) : (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#d1fae5',
                            color: '#059669',
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderRadius: '9999px'
                          }}>
                            <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Paid
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        {sortedCommissionData.length > 4 && (
          <div style={{ marginTop: '20px', marginBottom: '20px', display: 'flex', justifyContent: 'center', padding: '0 24px' }}>
            <button
              onClick={() => setCommissionExpanded(!commissionExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                flex: 1,
                maxWidth: isMobile ? 'none' : '240px',
                padding: '12px 24px',
                border: '1px solid #e5e5e5',
                borderRadius: '9999px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#1a1a1a',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              {commissionExpanded ? (
                <>
                  Show less
                  <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </>
              ) : (
                <>
                  Show all ({sortedCommissionData.length})
                  <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        )}
      </Card>

      {/* Collection Modal */}
      {collectionModal.open && (
        <CollectionModal
          staff={collectionModal.staff}
          salonId={salonId}
          onClose={() => setCollectionModal({ open: false, staff: null })}
          onSuccess={handleCollectionSuccess}
          isSelfEmployed={isSelfEmp}
        />
      )}

      {/* Commission Detail Sidebar */}
      {selectedCommissionStaff && (
        <>
          <div
            className="sheet-anim-backdrop"
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 1000 }}
            onClick={() => setSelectedCommissionStaff(null)}
          />
          <CommissionDetailSidebar
            staff={selectedCommissionStaff}
            onClose={() => setSelectedCommissionStaff(null)}
          />
        </>
      )}
    </div>
  );
};

export default CommissionsView;
